/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { ItemAvailability, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { IFullOrgUnitAvailability } from '../common/full-org-unit-availability';
import { IFarmlandsStoreSelectorResources } from '../farmlands-store-selector.props.autogenerated';
import { IStoreSelectorLocationLineItemResources, StoreSelectorLocationLineItemComponent } from './store-selector-location-line-item';

export interface IStoreSelectorLocationLinesProps {
    locations?: IFullOrgUnitAvailability[];
    resources: IFarmlandsStoreSelectorResources;

    alreadySelectedLocation?: OrgUnitLocation;
    outOfStockThreshold: number;
    enableOutOfStockCheck: boolean;

    onLocationSelected(location: OrgUnitLocation | undefined): void;
}

/**
 * Displays all the location line items, or null if none exists
 */
export class StoreSelectorLocationLines extends React.PureComponent<IStoreSelectorLocationLinesProps> {
    public render(): JSX.Element | null {
        if (!this.props.locations || this.props.locations.length === 0) {
            return null;
        }

        return (
            <div className='ms-store-select__locations'>
                {this.props.locations.map((availabilityWithHours, index) =>
                    availabilityWithHours && availabilityWithHours.OrgUnitAvailability && availabilityWithHours.OrgUnitAvailability.OrgUnitLocation && (
                        <div className='ms-store-select__location' key={index}>
                            <StoreSelectorLocationLineItemComponent
                                location={availabilityWithHours.OrgUnitAvailability.OrgUnitLocation}
                                storeHours={availabilityWithHours.StoreHours}
                                resources={this._mapResources(this.props.resources)}
                                isCurrentLocation={this._isCurrentLocation(availabilityWithHours.OrgUnitAvailability.OrgUnitLocation)}
                                isInStock={this._isProductInStock(availabilityWithHours.OrgUnitAvailability.ItemAvailabilities)}
                                handlers={{
                                    onSelected: this.props.onLocationSelected
                                }}
                                stockStatusLabel={this._stockInventoryLabel(availabilityWithHours)}
                            />
                        </div>
                    )
                )}
            </div>
        );
    }

    private _isCurrentLocation = (location: OrgUnitLocation | undefined): boolean =>
        (this.props.alreadySelectedLocation && location && this.props.alreadySelectedLocation.OrgUnitNumber === location.OrgUnitNumber) || false;

    private _isProductInStock = (ItemAvailabilities: ItemAvailability[] | undefined): boolean => {
        if (!this.props.enableOutOfStockCheck) {
            return true;
        }

        if (ItemAvailabilities && ItemAvailabilities.length > 0) {
            return ItemAvailabilities[0].AvailableQuantity ? ItemAvailabilities[0].AvailableQuantity > 0 : false;
        }

        return false;
    };

    private _stockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours?.ProductInventoryInformation
            && availabilityWithHours?.ProductInventoryInformation.length > 0) {
            for (let i = 0; i < availabilityWithHours.ProductInventoryInformation.length; i++) {
                if (availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId === availabilityWithHours.ProductInventoryInformation[i].InventLocationId) {
                    return availabilityWithHours.ProductInventoryInformation[i].StockLevelLabel;
                }

            }
        }

        return undefined;
    };

    private _mapResources = (resources: IFarmlandsStoreSelectorResources): IStoreSelectorLocationLineItemResources => {
        return {
            distanceUnit: resources.unitText,

            contactInfoHeader: resources.contactText,
            storeHoursHeader: resources.timeText,
            availabilityInfoHeader: resources.availabilityText,

            closedText: resources.storeHoursClosedText,
            outOfStock: resources.outOfStockText,
            inStock: resources.inStockText,

            currentLocation: resources.selectedStoreLocationText,
            selectLocation: resources.selectStoreText,
            selectLocationAriaLabelFormat: resources.selectStoreAriaFormatText,

            days: {
                monday: resources.storeHoursMondayText,
                tuesday: resources.storeHoursTuesdayText,
                wednesday: resources.storeHoursWednesdayText,
                thursday: resources.storeHoursThursdayText,
                friday: resources.storeHoursFridayText,
                saturday: resources.storeHoursSaturdayText,
                sunday: resources.storeHoursSundayText,
                mondayFull: resources.storeHoursMondayFullText,
                tuesdayFull: resources.storeHoursTuesdayFullText,
                wednesdayFull: resources.storeHoursWednesdayFullText,
                thursdayFull: resources.storeHoursThursdayFullText,
                fridayFull: resources.storeHoursFridayFullText,
                saturdayFull: resources.storeHoursSaturdayFullText,
                sundayFull: resources.storeHoursSundayFullText
            }
        };
    }
}